import type { CaseType } from "./deriveCaseType";

export interface ResolvedPreviewStain {
  id: string;
  selectionId?: string;
  markAsCompleted?: boolean;
  panelSelectionKey?: string;
  panelComponentId?: string;
}

export interface SubmittedResolvedStain {
  stainId: string;
  isCompleted: boolean;
  selectionId?: string;
  panelSelectionKey?: string;
  panelComponentId?: string;
}

interface SelectableStain {
  id: string;
  code: string;
  description: string;
  isActive: boolean;
}

export function buildActiveStainOptions(stains: SelectableStain[]) {
  return stains
    .filter((stain) => stain.isActive)
    .map((stain) => ({
      value: stain.id,
      label: `${stain.code} - ${stain.description}`,
    }));
}

export function buildStainSelectionId(
  source: "default" | "work-list" | "fallback" | "legacy-manual",
  specimenIndex: number,
  stainIndex: number,
  stainId: string
): string {
  return `${source}:${specimenIndex}:${stainIndex}:${stainId}`;
}

export function getStainSelectionId(stain: ResolvedPreviewStain): string {
  return stain.selectionId ?? stain.id;
}

export function shouldFetchSourceDefaultStains(
  accessioningType: CaseType | undefined,
  hasWorkListOverride: boolean
): boolean {
  return (
    accessioningType !== "tc_only" &&
    accessioningType !== "grossing_only" &&
    !hasWorkListOverride
  );
}

export function deduplicatePreviewStains<T extends ResolvedPreviewStain>(
  stains: T[]
): T[] {
  const uniqueStains = stains.reduce((stainsById, stain) => {
    const existing = stainsById.get(stain.id);
    if (!existing) {
      stainsById.set(stain.id, stain);
    } else if (stain.markAsCompleted && !existing.markAsCompleted) {
      stainsById.set(stain.id, { ...existing, markAsCompleted: true });
    }
    return stainsById;
  }, new Map<string, T>());
  return Array.from(uniqueStains.values());
}

export function buildResolvedStainSelections(
  specimenCount: number,
  resolvedStains: Record<number, ResolvedPreviewStain[]>,
  effectiveCompletions: Record<number, Record<string, boolean>>
): Record<number, SubmittedResolvedStain[]> {
  const selections: Record<number, SubmittedResolvedStain[]> = {};

  for (
    let specimenIndex = 0;
    specimenIndex < specimenCount;
    specimenIndex += 1
  ) {
    const stains = resolvedStains[specimenIndex];
    if (stains) {
      selections[specimenIndex] = stains
        .filter((stain) => !stain.panelSelectionKey)
        .map((stain) => ({
          stainId: stain.id,
          ...(stain.selectionId ? { selectionId: stain.selectionId } : {}),
          isCompleted:
            effectiveCompletions[specimenIndex]?.[getStainSelectionId(stain)] ??
            effectiveCompletions[specimenIndex]?.[stain.id] ??
            stain.markAsCompleted ??
            false,
        }));
    }
  }

  return selections;
}
